// NEW_FILE_JUSTIFIED: Bulk import hook — manages file upload, AI extraction, matching, and batch execute flow
import { useState, useCallback, useMemo } from 'react';
import { useMutation, useAction, useQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a local convex/ directory file
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks need convex Id type
import type {
  TBulkImportStep,
  IBulkImportPolicyMatch,
  TBulkPolicyAction,
  IBulkImportProgress,
} from '@/@types/bulkImport';
import type { IOrgInsuranceType } from '@/@types/insurance';

// LOCAL_TYPE_JUSTIFIED: Internal hook state — only used within this hook
interface IBulkImportHookState {
  step: TBulkImportStep;
  sessionId: Id<'bulkImportSessions'> | null;
  uploadedFileNames: string[];
  actionOverrides: Record<number, TBulkPolicyAction>;
  typeOverrides: Record<number, string>;
  error: string | null;
  progress: IBulkImportProgress;
}

const initialState: IBulkImportHookState = {
  step: 'upload',
  sessionId: null,
  uploadedFileNames: [],
  actionOverrides: {},
  typeOverrides: {},
  error: null,
  progress: { totalCreated: 0, totalEnriched: 0, totalSkipped: 0, totalErrors: 0 },
};

/**
 * Auto-match an extracted policyType string to an org insurance type ID.
 * Returns the matched type ID or undefined if no match.
 */
function autoMatchInsuranceType(
  extractedType: string | undefined,
  orgTypes: IOrgInsuranceType[]
): string | undefined {
  if (!extractedType || orgTypes.length === 0) return undefined;
  const lower = extractedType.toLowerCase();

  // Direct name match
  const directMatch = orgTypes.find(t => t.name.toLowerCase() === lower);
  if (directMatch) return directMatch._id;

  // Keyword matching
  for (const t of orgTypes) {
    const tName = t.name.toLowerCase();
    if (
      (lower.includes('third party') || lower.includes('tp') || lower.includes('act')) &&
      (tName.includes('car') ||
        tName.includes('motor') ||
        tName.includes('vehicle') ||
        tName.includes('auto'))
    )
      return t._id;
    if (
      (lower.includes('comprehensive') || lower.includes('comp') || lower.includes('gold')) &&
      (tName.includes('car') ||
        tName.includes('motor') ||
        tName.includes('vehicle') ||
        tName.includes('auto'))
    )
      return t._id;
    if (lower.includes('home') && tName.includes('home')) return t._id;
    if (lower.includes('health') && tName.includes('health')) return t._id;
    if (lower.includes('travel') && tName.includes('travel')) return t._id;
    if (lower.includes('life') && tName.includes('life')) return t._id;
  }

  // Fallback: if only one type exists, use it
  if (orgTypes.length === 1) return orgTypes[0]._id;

  return undefined;
}

export function useBulkImport(orgId: Id<'organisations'> | null, orgTypes: IOrgInsuranceType[]) {
  const [state, setState] = useState<IBulkImportHookState>(initialState);

  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const createSession = useMutation(api['insurance/bulkImportSession'].createSession);
  const extractBulkPolicies = useAction(api['insurance/bulkImport'].extractBulkPolicies);
  const executeBulkImportAction = useAction(api['insurance/bulkImport'].executeBulkImport);

  // Reactive query for match results
  const matchResultsQuery = useQuery(
    api['insurance/bulkImportSession'].matchExtractedPolicies,
    state.sessionId && orgId && (state.step === 'review' || state.step === 'processing')
      ? { orgId, sessionId: state.sessionId }
      : 'skip'
  );

  // Merge server query results with local action overrides + auto-matched types
  const currentMatchResults: IBulkImportPolicyMatch[] = useMemo(() => {
    if (!matchResultsQuery) return [];
    return (matchResultsQuery as IBulkImportPolicyMatch[]).map((match, index) => {
      // AS_JUSTIFIED: query returns flexible validator shape — verified as IBulkImportPolicyMatch[]
      const actionOverride = state.actionOverrides[index];
      const typeOverride = state.typeOverrides[index];
      const autoType = autoMatchInsuranceType(match.extracted.policyType, orgTypes);
      return {
        ...match,
        action: actionOverride ?? match.action,
        assignedTypeId: typeOverride ?? autoType,
      };
    });
  }, [matchResultsQuery, state.actionOverrides, state.typeOverrides, orgTypes]);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const uploadAndExtract = useCallback(
    async (files: File[]) => {
      if (!orgId || files.length === 0) return;

      setState(prev => ({
        ...prev,
        step: 'extracting',
        uploadedFileNames: files.map(f => f.name),
        error: null,
      }));

      try {
        const fileIds: Id<'_storage'>[] = [];
        const fileNames: string[] = [];

        for (const file of files) {
          const uploadUrl = await createUploadUrl({});
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });
          const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response is always { storageId: Id<'_storage'> }
          fileIds.push(storageId);
          fileNames.push(file.name);
        }

        const sessionId = await createSession({ orgId, fileIds, fileNames });
        setState(prev => ({ ...prev, sessionId }));

        const result = await extractBulkPolicies({ sessionId, storageIds: fileIds });

        if (result.success) {
          setState(prev => ({ ...prev, step: 'review' }));
        } else {
          setState(prev => ({
            ...prev,
            step: 'error',
            error: result.error ?? 'Extraction failed',
          }));
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setState(prev => ({ ...prev, step: 'error', error: message }));
      }
    },
    [orgId, createUploadUrl, createSession, extractBulkPolicies]
  );

  const updateAction = useCallback((index: number, action: TBulkPolicyAction) => {
    setState(prev => ({
      ...prev,
      actionOverrides: { ...prev.actionOverrides, [index]: action },
    }));
  }, []);

  const updateActions = useCallback(
    (updates: Array<{ index: number; action: TBulkPolicyAction }>) => {
      setState(prev => {
        const newOverrides = { ...prev.actionOverrides };
        for (const u of updates) {
          newOverrides[u.index] = u.action;
        }
        return { ...prev, actionOverrides: newOverrides };
      });
    },
    []
  );

  const updateType = useCallback((index: number, typeId: string) => {
    setState(prev => ({
      ...prev,
      typeOverrides: { ...prev.typeOverrides, [index]: typeId },
    }));
  }, []);

  const hasUnassignedTypes = useMemo(() => {
    return currentMatchResults.some(m => m.action === 'create' && !m.assignedTypeId);
  }, [currentMatchResults]);

  const execute = useCallback(async () => {
    if (!orgId || !state.sessionId) return;

    setState(prev => ({ ...prev, step: 'processing' }));

    try {
      const actions = currentMatchResults.map((match, index) => ({
        index,
        action: match.action,
        existingPolicyId: match.existingPolicyId as Id<'insurancePolicies'> | undefined, // AS_JUSTIFIED: existingPolicyId comes from matchExtractedPolicies
        insuranceTypeId: match.assignedTypeId as Id<'orgInsuranceTypes'> | undefined, // AS_JUSTIFIED: assignedTypeId comes from orgTypes IDs
      }));

      const result = await executeBulkImportAction({
        sessionId: state.sessionId,
        orgId,
        actions,
      });

      setState(prev => ({
        ...prev,
        step: 'completed',
        progress: {
          totalCreated: result.totalCreated,
          totalEnriched: result.totalEnriched,
          totalSkipped: result.totalSkipped,
          totalErrors: result.totalErrors,
        },
        error: result.errors.length > 0 ? result.errors.join('\n') : null,
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Execution failed';
      setState(prev => ({ ...prev, step: 'error', error: message }));
    }
  }, [orgId, state.sessionId, currentMatchResults, executeBulkImportAction]);

  return {
    step: state.step,
    sessionId: state.sessionId,
    uploadedFileNames: state.uploadedFileNames,
    matchResults: currentMatchResults,
    error: state.error,
    progress: state.progress,
    hasUnassignedTypes,
    uploadAndExtract,
    updateAction,
    updateActions,
    updateType,
    execute,
    reset,
  };
}
